import { get } from 'svelte/store';
// Formating
import { format } from 'svelte-i18n';
//Helpers
import { getTariffByID } from 'src/helpers/power_prices_helpers';
import { getSystemStyle, getGradientColor } from 'src/helpers/statistics/ui/chart_colors_helpers';
import { getStatistics } from 'src/helpers/statistics/request/chart_get_statistics_helpers';
import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';
import { capFirstChar, convert } from 'src/helpers/utilities';
import { convertCost } from 'src/helpers/units_helpers';
import { devices } from 'src/stores/lists/devices';
import { home_data, user_settings } from 'src/stores/account/user';
import { SystemStyles } from '@Shelly/shelly-design-system';
import { delete_home } from 'src/helpers/home_helpers';
import {
	calculateBackUpSetup,
	calculateBalconyPVSetup,
	calculateParallelSetup,
} from './pv_setup_statistics_helper';
//Types
import type { ChartControlsDataInterface } from 'src/helpers/statistics/ui/chart_control_helpers';
import type { Device } from 'src/types/Device';
import type {
	ChartConfig,
	ChartConfigInterface,
	ChartDataInterface,
	ChartStatisticRangePeriod,
	ChartVariantsType,
	StatisticsData,
	TotalConsumptionItem,
} from 'src/types/Chart';

import type {
	GetStatsResponseData,
	GetStatsResponseHistoryData,
	PVSetupData,
} from 'src/types/requests/charts/get_stats_period';
import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';

export const getPVSetupTypes = (): PVSetupTypesWithTranslation[] => {
	const $format = get(format);
	return [
		{
			title: 'Parallel',
			translation: $format('pv_setup_type_parallel', {
				default: 'Parallel',
			}),
		},
		{
			title: 'BackUP',
			translation: $format('pv_setup_type_backup', { default: 'BackUP' }),
		},
		{
			title: 'BalconyPV',
			translation: $format('pv_setup_type_balcony', {
				default: 'BalconyPV',
			}),
		},
	];
};

export const getPVSetupByType = async (
	gridSourcesDevices: Device[],
	generatedEnergyDevices: Device[],
	type: PVSetupTypes,
	chartConfig: ChartConfig,
	chartControlsData: ChartControlsDataInterface,
): Promise<ChartDataInterface> => {
	const devices = [...generatedEnergyDevices, ...gridSourcesDevices];
	const netMeteringPeriod = 0; // TODO: PV setup Net-metering adaptaion https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/2663
	const resDataAll: StatisticsData[] = await Promise.all(
		devices.map((dev) => {
			const deviceChartConfig: ChartConfigInterface = getDeviceChartsConfig(dev).find(
				(i) => i?.charts?.[0]?.name === 'consumption',
			);
			return getStatistics(
				dev,
				chartControlsData,
				deviceChartConfig?.charts?.[0],
				deviceChartConfig?.url,
				netMeteringPeriod,
			);
		}),
	);
	const pvSetupData: PVSetupData = { history: [] };
	const calculators = {
		BackUP: calculateBackUpSetup,
		Parallel: calculateParallelSetup,
		BalconyPV: calculateBalconyPVSetup,
	};
	resDataAll.forEach((data) => calculators[type]?.(data, pvSetupData));

	const chartData = getPVSetupChartData(
		pvSetupData,
		chartConfig,
		chartControlsData.rangeOption.period,
	);

	return chartData;
};

export const getPVSetupChartsConfig = (type: ChartVariantsType = 'bar'): ChartConfig => {
	return {
		name: 'pv_setup_consumption',
		type,
		series: [
			{
				visible: true,
				name: 'grid_source',
				style: 'grid_source',
				stack: 'pvSetup',
			},
			{
				visible: true,
				name: 'generated_energy',
				style: 'generated_energy',
				stack: 'pvSetup',
			},
			{
				visible: true,
				name: 'batt_invertor',
				style: 'negative_energy',
				stack: 'pvSetup',
			},
			{
				visible: true,
				name: 'export_energy',
				stack: 'pvSetup',
				style: 'returned',
			},
			{
				visible: false,
				name: 'total_house_energy',
				style: 'total_house_energy',
			},
		],
		units: 'Wh',
		height: 300,
	};
};

export type PVSetupTypes = 'Parallel' | 'BackUP' | 'BalconyPV';

export type PVSetupTypesWithTranslation = {
	title: PVSetupTypes;
	translation: string;
};

const getPVSetupChartData = (
	data: GetStatsResponseData,
	chartConfig: ChartConfig,
	rangePeriod: ChartStatisticRangePeriod,
): ChartDataInterface => {
	const chartData: ChartDataInterface = {
		...chartConfig,
		rangePeriod,
		rangeInterval: data.interval,
		series: [],
	};
	const series = chartConfig.series,
		currency = get(user_settings).currency || '';
	let totalConsumption = 0,
		totalConsumptionCost = 0,
		totalHouseConsumption = 0,
		totalSolarReturned = 0,
		totalBattery = 0,
		totalExportEnergy = 0,
		missingData = true;
	for (let i = 0; i < series.length; ++i) {
		const item = series[i],
			historyData = data,
			gridSource = /^(grid_source)$/.test(item.name),
			history = historyData?.history;

		chartData.series[i] = { ...item, data: [] };
		if (!history) continue;
		const consumptionDataset = {};
		const chartName = item.name;
		history.map((obj: GetStatsResponseHistoryData) => {
			if (missingData && !obj?.missing) missingData = false;
			let value: number = obj?.missing ? 0 : obj[chartName],
				parts = obj.datetime.split(/[-\s:T]/),
				dt = Date.UTC(
					Number(parts[0]),
					Number(parts[1]) - 1,
					Number(parts[2]),
					Number(parts[3]),
					Number(parts[4]),
					0,
					0,
				);
			let costPower = null;
			let rawCost = 0;
			if (gridSource) {
				rawCost = obj?.grid_cost;
			} else if (/^(batt_invertor)$/.test(item.name)) {
				rawCost = obj?.batt_invertor_cost;
			} else if (/^(generated_energy)$/.test(item.name)) {
				rawCost = obj?.cost - obj?.grid_cost;
			} else if (/^(export_energy)$/.test(item.name)) {
				rawCost = obj?.export_energy_cost;
			} else {
				rawCost = obj?.cost - obj?.export_energy_cost;
			}
			if (rawCost) {
				const cost = convertCost(Math.abs(rawCost), currency),
					tariffID = obj.tariff_id || '0',
					tariffName = getTariffByID(tariffID)?.name,
					unit = 'Wh';
				costPower = {
					tariff: {
						id: tariffID,
						name: (tariffID != '0' && tariffName) || `Tariff ${tariffID}`,
						cost: rawCost,
						value,
						currency,
						unit,
					},
					cost:
						rawCost < 0
							? `<b class='text-danger'>${cost}</b>`
							: /^(generated_energy|export_energy)$/.test(item.name)
								? `<b class='text-success'>${cost}</b>`
								: cost,
					value: convert(Math.abs(value), unit),
					show: true,
				};
			}

			if (!consumptionDataset[dt]) {
				consumptionDataset[dt] = {
					dt,
					value,
					name: item.name,
					missing: Boolean(obj?.missing),
				};
				consumptionDataset[dt].cost = rawCost || 0;
				consumptionDataset[dt].metaData = [costPower];
			} else {
				consumptionDataset[dt].value += value;
				consumptionDataset[dt].missing += Boolean(obj?.missing);
				consumptionDataset[dt].cost += rawCost || 0;
				consumptionDataset[dt].metaData.push(costPower);
			}
		});
		if (Object.keys(consumptionDataset).length) {
			Object.keys(consumptionDataset)
				.sort()
				.forEach((key: string) => {
					const e = consumptionDataset[key];
					if (/^(grid_source)$/.test(item.name)) {
						totalConsumption += e.value;
						totalConsumptionCost += e.cost;
					} else if (/^(generated_energy)$/.test(item.name)) {
						totalSolarReturned += e.value;
						e.value = Math.max(e.value, 0);
					} else if (/^(total_house_energy)$/.test(item.name)) {
						totalHouseConsumption += e.value;
					} else if (/^(export_energy)$/.test(item.name)) {
						totalExportEnergy += e.value;
					} else if (/^(batt_invertor)$/.test(item.name)) {
						totalBattery += e.value;
					}
					chartData.series[i].data.push({
						...e,
						convertCost: convertCost(e.cost, currency),
					});
				});
			chartData.series[i].missingData = missingData;
		}
	}
	chartData.totalConsumption = getPVSetupLabels(
		totalConsumption,
		totalConsumptionCost,
		totalHouseConsumption,
		totalSolarReturned,
		totalBattery,
		totalExportEnergy,
	);

	return chartData;
};

const getPVSetupLabels = (
	totalConsumption: number,
	totalConsumptionCost: number,
	totalHouseConsumption: number,
	totalSolarReturned: number,
	totalBattery: number,
	totalExportEnergy: number,
): TotalConsumptionItem[] => {
	const $format = get(format);
	let labelListConsum = [];
	if (totalConsumption) {
		labelListConsum = [
			{
				icon: 'fas fa-bolt',
				value: convert(totalConsumption, 'Wh'),
				iconColor: getSystemStyle(SystemStyles.COLOR_PRIMARY),
				pieLabel: capFirstChar(
					$format('appliance_type_grid_source', {
						default: 'Grid source',
					}),
				),
				rawValue: totalConsumption,
				name: 'grid',
			},
		];
		if (totalConsumptionCost) {
			const sumCostFixed = convertCost(totalConsumptionCost, get(user_settings).currency);
			if (sumCostFixed) {
				labelListConsum.push({
					icon: 'fas fa-coins',
					value: `${sumCostFixed} `,
					iconColor: getSystemStyle(SystemStyles.COLOR_PRIMARY),
				});
			}
		}
	}
	if (totalHouseConsumption) {
		labelListConsum.unshift({
			icon: 'far fa-house',
			value: convert(totalHouseConsumption, 'Wh'),
			iconColor: getGradientColor('prev_period_reversed', '').solid,
			rawValue: totalHouseConsumption,
			name: 'house',
		});
	}
	if (totalSolarReturned) {
		labelListConsum.push({
			icon: `fas fa-solar-panel`,
			value: convert(Math.abs(totalSolarReturned), 'Wh'),
			iconColor: getGradientColor('generated_energy', '').solid,
			pieLabel: capFirstChar(
				$format('appliance_type_generated_energy', {
					default: 'Generated energy',
				}),
			),
			rawValue: Math.abs(totalSolarReturned),
			name: 'pv',
		});
	}
	if (totalBattery) {
		const batteryColor = getGradientColor('negative_energy', '').solid;
		labelListConsum.push({
			icon: `fa-duotone fa-light fa-battery`,
			value: convert(Math.abs(totalBattery), 'Wh'),
			iconStyle: `--fa-primary-color: ${batteryColor}; --fa-secondary-color: ${batteryColor}; --fa-secondary-opacity: 1;`,
			pieLabel: $format('pv_setup_batt_invertor', {
				default: 'Battery & invertor',
			}),
			rawValue: Math.abs(totalBattery),
			name: 'battery',
		});
	}
	if (totalExportEnergy) {
		labelListConsum.push({
			icon: `fas fa-utility-pole-double`,
			value: convert(Math.abs(totalExportEnergy), 'Wh'),
			iconColor: getGradientColor('returned', '').solid,
			pieLabel: capFirstChar(
				$format('pv_setup_set_exported_energy', {
					default: 'Exported energy',
				}),
			),
			rawValue: Math.abs(totalExportEnergy),
			name: 'exported',
		});
	}
	return labelListConsum;
};

export const getPVSetupWizardItems = () => {
	const $format = get(format),
		wizardStep = [
			{
				title: $format('pv_setup_configuration_select_type', {
					default: 'Type of PV',
				}),
				subtitle: $format('pv_setup_info_modal_subtitle', {
					default: 'How energy is calculated for the different PV modes',
				}),
			},
			{
				title: $format('pv_setup_select_grid_devices_title', {
					default: 'Grid devices',
				}),
				subtitle: $format('pv_setup_select_grid_devices_description', {
					default: 'Select the devices that are measuring the grid energy',
				}),
			},
			{
				title: $format('pv_setup_select_generated_energy_devices_title', {
					default: 'Generated energy devices',
				}),
				subtitle: $format('pv_setup_select_generated_energy_devices_description', {
					default: 'Select the devices that are measuring the generated energy',
				}),
			},
		];
	return wizardStep.map((item, i) => {
		return {
			...item,
			active: i === 0 ? true : false,
			stepNumber: i + 1,
			activeIndex: i,
		};
	});
};

export const getPVSetupDevices = (
	pvSetupType: PVSetupTypes,
): { gridDevices: Device[]; solarDevices: Device[] } => {
	const pvConfigurations = get(home_data)?.pv_configurations?.[pvSetupType] || {};
	const allDevices = get(devices);
	const gridDevices = [];
	const solarDevices = [];
	const pvConfigGridDevices = pvConfigurations?.gridDevices || [];
	const pvConfigSolarDevices = pvConfigurations?.solarDevices || [];
	for (const id in pvConfigGridDevices) {
		const dev: Device = allDevices[pvConfigGridDevices[id]];
		if (dev) {
			dev.pv_usage = 'grid';
			gridDevices.push(dev);
		}
	}
	for (const id in pvConfigSolarDevices) {
		const dev: Device = allDevices[pvConfigSolarDevices[id]];
		if (dev) {
			dev.pv_usage = 'solar';
			solarDevices.push(dev);
		}
	}
	return {
		gridDevices,
		solarDevices,
	};
};

export const getPVPieChartConfig = (labels: TotalConsumptionItem[]): ChartDataInterface => {
	const pieItems = labels.filter((i) => i.pieLabel);
	const pieLabels = pieItems.map((i) => i.pieLabel);
	return {
		name: 'pvPieChart',
		type: 'pie',
		units: 'W',
		labels: pieLabels,
		keys: pieItems.map((i) => i.name),
		loaded: pieLabels.length > 0,
		series: [
			{
				name: 'pvPieChart',
				visible: true,
				//@ts-ignore
				data: pieItems.map((i) => i.rawValue),
				colors: pieItems.map((i) => i.iconColor),
				style: 'none',
			},
		],
		height: 320,
	};
};

export const deletePvSetup = async (): Promise<CloudApiResponse> => {
	return await delete_home('pv_configurations');
};
